import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser, googleLogin } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const pending = JSON.parse(sessionStorage.getItem("pending_follow") || "null");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background blueprint-grid">
      <Navbar />
      <div className="max-w-md mx-auto px-4 pt-16 sm:pt-24 pb-16">
        <div className="border border-border rounded-xl bg-card p-8 fade-up">
          <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("login")}</h1>
          {pending?.name && (
            <p data-testid="login-follow-prompt" className="text-sm text-accent bg-accent/10 border border-accent/30 rounded-lg px-4 py-3 mt-4">
              {t("join_mosque_prompt").replace("{name}", pending.name)}
            </p>
          )}
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("password")}</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-accent hover:underline">{t("forgot_password")}</Link>
              </div>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full rounded-full active:scale-95">
              {loading ? "..." : t("login")}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase">{t("or")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" data-testid="google-login-btn" onClick={googleLogin} className="w-full rounded-full active:scale-95">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("continue_google")}
          </Button>
          <p className="text-sm text-muted-foreground text-center mt-6">
            {t("no_account")}{" "}
            <Link to="/register" data-testid="goto-register-link" className="text-accent hover:underline">{t("register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
